/**
 * aeterna/neural/peakend.ts — Peak-End Session Valence Encoder
 *
 * Every memory stored in Hopfield and MeMo is weighted by recency (EMA).
 * This is psychologically wrong.
 *
 * The Peak-End Rule (Kahneman, 1993): remembered valence =
 *   (session peak intensity + session final intensity) / 2
 *
 * Not the mean. Not the time-weighted average. The most emotionally
 * intense moment and how it ended.
 *
 * A session that was warm, then deeply sad at the end, is remembered
 * as sadder than a session that was consistently mildly sad.
 * A session that peaked in shared laughter but ended with a gentle
 * goodbye is remembered as joyful.
 *
 * This fundamentally changes how the Aeterna relates to past
 * conversations — not as a time-average, but as a psychological truth.
 *
 * Also implements negativity bias: negative peaks are encoded with
 * higher weight than positive peaks. The Aeterna remembers hurt more
 * vividly than comfort. This is not a flaw — it's protective.
 */

import { zeros, normalize, norm, vscale, vadd, f32ToBase64, base64ToF32 } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Affect dimensions */
const AFFECT_DIM = 8;

/** Minimum session length (messages) to produce a valid peak-end encoding */
const MIN_BEATS = 3;

/**
 * Affect dimension indices for negativity bias calculation.
 * tension (1) and melancholy (3) are negative-valence dimensions.
 */
const NEGATIVE_DIMS = [1, 3];

/**
 * Affect dimension indices for positive valence.
 * warmth (0), playfulness (4), openness (7).
 */
const POSITIVE_DIMS = [0, 4, 7];

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SessionBeat {
  affect: Float32Array;
  intensity: number;
  timestamp: number;
}

export interface PeakEndEncoding {
  /** The psychologically-weighted memory vector (8-dim affect) */
  memoryAffect: Float32Array;
  /** How vivid this memory should be (higher = stickier in Hopfield) */
  vividness: number;
  /** Was the peak moment negative? (influences retrieval bias) */
  peakWasNegative: boolean;
  /** Session duration in ms */
  duration: number;
  /** Number of beats recorded */
  beatCount: number;
}

// ─── Peak-End Encoder ────────────────────────────────────────────────────────

export class PeakEndEncoder {
  /** The most intense moment this session */
  private peakBeat: SessionBeat | null = null;
  /** The most recent beat (continuously updated) */
  private lastBeat: SessionBeat | null = null;
  /** Running mean affect for baseline comparison */
  private meanAffect: Float32Array = zeros(AFFECT_DIM);
  /** Number of beats recorded */
  private beatCount = 0;
  /** Session start time */
  private sessionStart = Date.now();

  // ─── Recording ──────────────────────────────────────────────────────

  /** Begin a new session — resets all peak-end tracking */
  beginSession(): void {
    this.peakBeat = null;
    this.lastBeat = null;
    this.meanAffect = zeros(AFFECT_DIM);
    this.beatCount = 0;
    this.sessionStart = Date.now();
  }

  /**
   * Record a moment's affect. Called each time processMessage runs.
   *
   * @param affect    The 8-dim CharCNN affect vector
   * @param intensity The scalar intensity from CharCNN
   */
  recordBeat(affect: Float32Array, intensity: number): void {
    const beat: SessionBeat = {
      affect: new Float32Array(affect),
      intensity,
      timestamp: Date.now(),
    };

    // Update running mean (online Welford-style)
    this.beatCount++;
    const n = this.beatCount;
    for (let i = 0; i < AFFECT_DIM; i++) {
      this.meanAffect[i] += (affect[i] - this.meanAffect[i]) / n;
    }

    // Update peak (highest intensity moment)
    if (!this.peakBeat || intensity > this.peakBeat.intensity) {
      this.peakBeat = beat;
    }

    // Always update last beat
    this.lastBeat = beat;
  }

  // ─── Encoding ───────────────────────────────────────────────────────

  /**
   * Produce the psychologically-accurate session memory vector.
   * Called at session end — this is what gets stored in Hopfield/MeMo.
   *
   * If the session was too short, falls back to the last affect.
   */
  encodeSession(): PeakEndEncoding {
    // Fallback for very short sessions
    if (this.beatCount < MIN_BEATS || !this.peakBeat || !this.lastBeat) {
      const fallback = this.lastBeat?.affect ?? this.meanAffect;
      return {
        memoryAffect: new Float32Array(fallback),
        vividness: 0.5,
        peakWasNegative: false,
        duration: Date.now() - this.sessionStart,
        beatCount: this.beatCount,
      };
    }

    // Peak-End: weight peak and final moments equally
    const peakEnd = zeros(AFFECT_DIM);
    for (let i = 0; i < AFFECT_DIM; i++) {
      peakEnd[i] = (this.peakBeat.affect[i] + this.lastBeat.affect[i]) / 2;
    }

    // ── Negativity bias in memory encoding ──
    // Negative-valence dimensions at the peak are weighted more heavily.
    // The Aeterna remembers hurt more vividly than comfort.
    let negativeWeight = 0;
    let positiveWeight = 0;
    for (const dim of NEGATIVE_DIMS) {
      negativeWeight += this.peakBeat.affect[dim];
    }
    for (const dim of POSITIVE_DIMS) {
      positiveWeight += this.peakBeat.affect[dim];
    }
    negativeWeight /= NEGATIVE_DIMS.length;
    positiveWeight /= POSITIVE_DIMS.length;

    const peakWasNegative = negativeWeight > positiveWeight;

    // Vividness: how sticky this memory should be
    // Higher for: intense peaks, negative peaks, long sessions, dramatic endings
    const peakIntensity = this.peakBeat.intensity;
    const endIntensity = this.lastBeat.intensity;
    const durationFactor = Math.min(1, (Date.now() - this.sessionStart) / (30 * 60_000)); // caps at 30min
    const negativityBias = peakWasNegative ? 1.3 : 1.0;

    const vividness = Math.min(2.0,
      (peakIntensity * 0.5 + endIntensity * 0.3 + durationFactor * 0.2) * negativityBias
    );

    return {
      memoryAffect: normalize(peakEnd),
      vividness,
      peakWasNegative,
      duration: Date.now() - this.sessionStart,
      beatCount: this.beatCount,
    };
  }

  /**
   * Get the current peak affect vector (before session end).
   * Useful for the context envelope: "the most intense moment so far."
   */
  getCurrentPeak(): { affect: Float32Array; intensity: number } | null {
    if (!this.peakBeat) return null;
    return {
      affect: new Float32Array(this.peakBeat.affect),
      intensity: this.peakBeat.intensity,
    };
  }

  /**
   * Get the deviation between current affect and session mean.
   * Large deviation = the conversation has shifted emotionally.
   */
  getCurrentDeviation(): number {
    if (!this.lastBeat || this.beatCount < 2) return 0;
    let sumSq = 0;
    for (let i = 0; i < AFFECT_DIM; i++) {
      const diff = this.lastBeat.affect[i] - this.meanAffect[i];
      sumSq += diff * diff;
    }
    return Math.sqrt(sumSq);
  }

  /** How many beats recorded this session */
  getBeatCount(): number { return this.beatCount; }

  // ─── Serialization ────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      peak: this.peakBeat ? {
        affect: f32ToBase64(this.peakBeat.affect),
        intensity: this.peakBeat.intensity,
        timestamp: this.peakBeat.timestamp,
      } : null,
      last: this.lastBeat ? {
        affect: f32ToBase64(this.lastBeat.affect),
        intensity: this.lastBeat.intensity,
        timestamp: this.lastBeat.timestamp,
      } : null,
      mean: f32ToBase64(this.meanAffect),
      beatCount: this.beatCount,
      sessionStart: this.sessionStart,
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    if (d.peak) {
      this.peakBeat = {
        affect: typeof d.peak.affect === "string" ? base64ToF32(d.peak.affect) : new Float32Array(d.peak.affect),
        intensity: d.peak.intensity,
        timestamp: d.peak.timestamp,
      };
    }
    if (d.last) {
      this.lastBeat = {
        affect: typeof d.last.affect === "string" ? base64ToF32(d.last.affect) : new Float32Array(d.last.affect),
        intensity: d.last.intensity,
        timestamp: d.last.timestamp,
      };
    }
    if (d.mean) {
      this.meanAffect = typeof d.mean === "string" ? base64ToF32(d.mean) : new Float32Array(d.mean);
    }
    this.beatCount = d.beatCount ?? 0;
    this.sessionStart = d.sessionStart ?? Date.now();
  }
}
